import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { ServiceInvoiceList } from "@cx/contracts";
import { SERVICE_INVOICE_STATUSES } from "@cx/contracts";
import { formatBRL, formatCompetence } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { ENVIRONMENT_LABEL } from "../invoices/labels";
import { NFSE_STATUS_LABEL, NFSE_STATUS_TONE, isoDateBR } from "./labels";

/** Serviços › Notas de serviço (RF-SER-04): NFS-e avulsas e das cobranças de contratos. */
export function ServiceInvoicesListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const term = q.trim();
  const canCreate = can(perms, "invoices", "create");
  const list = useInfiniteQuery({
    queryKey: ["service-invoices", companyId, "list", term, status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ServiceInvoiceList>(
        `/service-invoices?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(status ? { status } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const create = () => navigate({ to: "/servicos/notas-de-servico/nova" });

  return (
    <>
      <ContentHeader
        title="Notas de serviço"
        breadcrumb={["Serviços", "Notas de serviço"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={create}>
              Incluir NFS-e
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-5">
            <label className="visually-hidden" htmlFor="si-q">
              Pesquisar NFS-e
            </label>
            <input
              id="si-q"
              type="search"
              className="form-control"
              placeholder="Número ou tomador"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="si-status">
              Situação
            </label>
            <select
              id="si-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Todas as situações</option>
              {SERVICE_INVOICE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {NFSE_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma nota de serviço"
            description="Emita NFS-e avulsas ou pelas cobranças dos contratos."
            actions={canCreate ? [{ label: "Incluir NFS-e", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Notas de serviço">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Emissão</th>
                    <th scope="col">Tomador</th>
                    <th scope="col">Origem</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/servicos/notas-de-servico/$id" params={{ id: r.id }}>
                          {r.nfseNumber ?? (r.rpsNumber ? `RPS ${r.rpsNumber}` : "Rascunho")}
                        </Link>
                        {r.environment === 2 && (
                          <div className="small text-muted">{ENVIRONMENT_LABEL[2]}</div>
                        )}
                      </td>
                      <td>{r.issuedAt ? isoDateBR(r.issuedAt) : "—"}</td>
                      <td>{r.recipientName}</td>
                      <td>
                        {r.origin?.type === "service_order"
                          ? `OS nº ${r.origin.number}`
                          : r.origin
                            ? `Contrato nº ${r.origin.contractNumber} — ${formatCompetence(r.origin.competence)}`
                            : "Avulsa"}
                      </td>
                      <td className="text-end">{formatBRL(r.servicesCents)}</td>
                      <td>
                        <StatusBadge tone={NFSE_STATUS_TONE[r.status]}>
                          {NFSE_STATUS_LABEL[r.status]}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="p-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
